"use client";

/**
 * AnonProjectResumeNotice — resume CTA for a logged-out visitor who already has
 * a live anonymous project (LEV-1594 / LEV-1773). Rendered in place of the
 * onboarding intake on the home hero and the partner landers, so both surfaces
 * share one implementation and one set of analytics.
 *
 * `textTheme` follows the hero's own convention: "dark" = dark text (light
 * hero background), "light" = white text (dark/orange hero background).
 *
 * With `program` it renders as the program lander's card instead (LEV-2722):
 * the lander keeps its own headline and countdown, so this is a card under an
 * h2, not a replacement hero. It opens the program's application through
 * sign-up and the claim (anonProgramResumeHref); same analytics either way.
 */

import { useEffect, useRef } from "react";
import Link from "next/link";
import {
  trackAnonProjectResumeNoticeShown,
  trackAnonProjectResumeCanvasClicked,
} from "@/lib/analytics/client";
import type { LandingPageSlug } from "@/lib/analytics/events";
import type { ProgramPageConfig } from "@/constants/programs";
import { anonProgramResumeHref } from "@/lib/anonymousProject/claimRedirect";

export interface AnonProjectResumeNoticeProps {
  projectId: string;
  landingPageSlug: LandingPageSlug;
  textTheme?: "light" | "dark";
  /**
   * When false, the component renders but suppresses the impression event until
   * the prop flips to true. Callers pass `isSignedIn === false` so the event
   * waits for a resolved signed-out signal — a visitor who turns out to be
   * signed in must never receive a false impression. Defaults to true (fires
   * immediately on mount, matching legacy behaviour for callers that already
   * gate the render on a resolved signed-out state).
   */
  trackImpression?: boolean;
  /** Set on a program lander: names the program and renders the card form. */
  program?: Pick<ProgramPageConfig, "name" | "slug">;
}

export default function AnonProjectResumeNotice({
  projectId,
  landingPageSlug,
  textTheme = "dark",
  trackImpression = true,
  program,
}: AnonProjectResumeNoticeProps) {
  const isDarkText = textTheme === "dark";
  // Fires at most once per component lifetime. The ref guard survives
  // StrictMode's remount. Including trackImpression in deps lets the effect
  // re-run when the prop flips false→true (Clerk settling), while the ref
  // blocks a second fire if the dep array changes for any other reason.
  const impressionFiredRef = useRef(false);

  useEffect(() => {
    if (!trackImpression || impressionFiredRef.current) return;
    impressionFiredRef.current = true;
    trackAnonProjectResumeNoticeShown({ landingPageSlug });
  }, [landingPageSlug, trackImpression]);

  const trackContinueClick = () => trackAnonProjectResumeCanvasClicked({ landingPageSlug });

  if (program) {
    return (
      <div className="flex w-full flex-col items-start gap-5 rounded-[29px] border border-landing-bg-medium bg-white p-6 text-left shadow-[0_8px_32px_rgba(0,0,0,0.08)] md:p-8">
        <h2 className="text-[24px] font-medium leading-[1.2] tracking-[-0.02em] text-landing-dark md:text-[28px]">
          Your {program.name} draft is waiting
        </h2>
        <Link
          href={anonProgramResumeHref(projectId, program.slug)}
          className="inline-flex min-h-11 items-center gap-2 rounded-full bg-landing-dark px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-[#3D4149]"
          onClick={trackContinueClick}
          data-testid="resume-canvas-link"
        >
          Continue your application
        </Link>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-6 text-center">
      <h1
        className={`text-[36px] md:text-[48px] tracking-[-0.96px] leading-[1.1] ${
          isDarkText ? "text-landing-dark" : "text-white"
        }`}
      >
        You&apos;re already building with Lev.
      </h1>
      <p
        className={`text-lg md:text-xl leading-[1.4] ${
          isDarkText ? "text-landing-secondary" : "text-white/90"
        }`}
      >
        Pick up where you left off.
      </p>
      <Link
        href={`/${projectId}`}
        className={`inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-medium transition-colors ${
          isDarkText
            ? "bg-landing-dark text-white hover:bg-[#3D4149]"
            : "bg-white text-landing-dark hover:bg-white/90"
        }`}
        onClick={trackContinueClick}
        data-testid="resume-canvas-link"
      >
        Continue to your project
      </Link>
    </div>
  );
}
